import React, { useState, useEffect } from "react";
import {
  Layout, Table, Button, Space, Select, Input, InputNumber, Row, Col, Tag, message, Descriptions, Card, Spin, Popconfirm
} from "antd";
import { useParams, useNavigate } from "react-router-dom";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
  AuditOutlined, SaveOutlined, PlusOutlined, DeleteOutlined, InfoCircleOutlined, SendOutlined, EyeOutlined
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import "../../../styles/fluidDesign.css";
import AppBreadcrumb from "../../../components/common/AppBreadcrumb";
import SupplierDetailsModal from "../../../components/common/SupplierDetailsModal";
import { getStatusColor } from "../../../utils/statusHelper";

const { Content } = Layout;
const { Option } = Select;

const ReceiveGRNItems = () => {
  const { poId } = useParams();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(window.location.search);
  const supplierId = searchParams.get("supplierId");
  const grnId = searchParams.get("grnId");

  const [poMaster, setPoMaster] = useState(null);
  const [loadedGrnMaster, setLoadedGrnMaster] = useState(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const [supplierModalVisible, setSupplierModalVisible] = useState(false);

  // GRN Items list (state-managed like ManagePRItems)
  const [grnItems, setGrnItems] = useState([]);

  // Add item form fields
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [addQty, setAddQty] = useState(1);
  const [addCost, setAddCost] = useState(0);
  const [addTax, setAddTax] = useState(0);
  const [addDiscount, setAddDiscount] = useState(0);

  // Receipt config
  const [supRefDate, setSupRefDate] = useState(dayjs());
  const [supRef, setSupRef] = useState("");

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchInventoryItems();
      if (grnId) {
        await fetchGRNData();
      } else {
        // Fallback: If grnId is missing, create/get draft and redirect
        try {
          const payload = {
            grn_status: "Draft",
            items: []
          };
          if (poId !== "0") {
            payload.pr_id = parseInt(poId);
          } else {
            payload.pr_id = 0;
            payload.sup_id = parseInt(supplierId);
          }
          const res = await api.post("/inventory/grn/create_from_pr.php", payload);
          if (res.data && res.data.status === "success") {
            if (poId !== "0") {
              navigate(`/grn/receive/${poId}?grnId=${res.data.grn_id}`, { replace: true });
            } else {
              navigate(`/grn/receive/0?supplierId=${supplierId}&grnId=${res.data.grn_id}`, { replace: true });
            }
          } else {
            message.error(res.data.message || "Failed to initialize draft GRN.");
            navigate("/grn/list");
          }
        } catch (error) {
          message.error("Failed to initialize draft GRN.");
          navigate("/grn/list");
        }
      }
      setLoading(false);
    };
    init();
  }, [poId, grnId, supplierId]);

  const fetchGRNData = async () => {
    try {
      const res = await api.get(`/inventory/grn/get.php?id=${grnId}`);
      if (res.data && res.data.status === "success") {
        const master = res.data.master;
        setLoadedGrnMaster(master);
        setSupRefDate(master.sup_ref_date ? dayjs(master.sup_ref_date) : dayjs());
        setSupRef(master.sup_ref || "");

        if (Number(master.is_from_pr) === 1 || Number(master.pr_id) > 0) {
          const resPO = await api.get(`/inventory/pr/get.php?id=${master.pr_id}`);
          if (resPO.data && resPO.data.status === "success") {
            setPoMaster(resPO.data.master);
            const prItems = resPO.data.items || [];
            const grnItemsMapped = res.data.items.map(item => ({
              invi_id: Number(item.invi_id),
              inv_code: item.inv_code,
              inv_desc: item.inv_desc,
              grn_qty: parseFloat(item.grn_qty),
              cost_price: parseFloat(item.cost_price),
              tax_price: parseFloat(item.tax_price),
              discount_price: parseFloat(item.discount_price),
              grnitem_note: item.grnitem_note || "",
              is_adhoc: false,
              ordered_qty: 0,
              already_received: 0,
              remaining_qty: 0
            }));

            prItems.forEach(prItem => {
              const matchedGrnItem = grnItemsMapped.find(gi => gi.invi_id === Number(prItem.invi_id));
              const ordered = parseFloat(prItem.order_qty) || 0;
              const prevReceived = parseFloat(prItem.received_qty) || 0;
              const note = prItem.item_note || "";
              if (matchedGrnItem) {
                matchedGrnItem.ordered_qty = ordered;
                matchedGrnItem.already_received = prevReceived;
                matchedGrnItem.remaining_qty = Math.max(0, ordered - prevReceived);
                matchedGrnItem.item_note = note;
              } else {
                grnItemsMapped.push({
                  invi_id: Number(prItem.invi_id),
                  inv_code: prItem.inv_code,
                  inv_desc: prItem.inv_desc,
                  grn_qty: 0,
                  cost_price: parseFloat(prItem.unit_price) || 0,
                  tax_price: parseFloat(prItem.tax_price) || 0,
                  discount_price: parseFloat(prItem.discount_price) || 0,
                  grnitem_note: "",
                  is_adhoc: false,
                  ordered_qty: ordered,
                  already_received: prevReceived,
                  remaining_qty: Math.max(0, ordered - prevReceived),
                  item_note: note
                });
              }
            });
            setGrnItems(grnItemsMapped);
          }
        } else {
          setGrnItems(res.data.items.map(item => ({
            invi_id: Number(item.invi_id),
            inv_code: item.inv_code,
            inv_desc: item.inv_desc,
            grn_qty: parseFloat(item.grn_qty),
            cost_price: parseFloat(item.cost_price),
            tax_price: parseFloat(item.tax_price),
            discount_price: parseFloat(item.discount_price),
            grnitem_note: item.grnitem_note || "",
            is_adhoc: true
          })));
        }
        setDataReady(true);
      } else {
        message.error("Failed to load draft GRN details.");
      }
    } catch (error) {
      message.error("Error loading draft GRN details.");
    }
  };

  const fetchInventoryItems = async () => {
    try {
      const res = await api.get("/inventory/items/list.php?page=1&limit=1000");
      if (res.data && res.data.status === "success") {
        setInventoryItems(res.data.dataset || []);
      }
    } catch (error) {
      message.error("Failed to load inventory items.");
    }
  };

  // Add Item Handler (ManagePRItems style)
  const handleItemSelect = (itemId) => {
    setSelectedItemId(itemId);
    const selected = inventoryItems.find(i => Number(i.invi_id) === Number(itemId));
    if (selected) {
      setAddQty(1);
      setAddCost(parseFloat(selected.cost_price) || 0);
      setAddTax(0);
      setAddDiscount(0);
    }
  };

  const syncGRNItemsToDB = async (updatedItems) => {
    try {
      const itemsToSend = updatedItems.filter(i => i.invi_id);
      const payload = {
        grn_id: grnId ? parseInt(grnId) : undefined,
        pr_id: loadedGrnMaster ? parseInt(loadedGrnMaster.pr_id) : parseInt(poId),
        sup_ref_date: supRefDate ? supRefDate.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
        grn_status: "Draft",
        items: itemsToSend.map(i => ({
          invi_id: parseInt(i.invi_id),
          grn_qty: parseFloat(i.grn_qty || 0),
          cost_price: parseFloat(i.cost_price || 0),
          tax_price: parseFloat(i.tax_price || 0),
          discount_price: parseFloat(i.discount_price || 0),
          grnitem_note: i.grnitem_note || ""
        }))
      };
      if (poId === "0" || (loadedGrnMaster && Number(loadedGrnMaster.is_from_pr) === 0)) {
        payload.sup_id = loadedGrnMaster ? parseInt(loadedGrnMaster.sup_id) : parseInt(supplierId);
      }
      payload.sup_ref = supRef;

      const res = await api.post("/inventory/grn/create_from_pr.php", payload);
      if (res.data && res.data.status !== "success") {
        message.error(res.data.message || "Failed to sync items to database.");
      }
    } catch (error) {
      message.error("Failed to sync items to database.");
    }
  };

  const handleAddItem = () => {
    if (!selectedItemId) { message.warning("Please select an item."); return; }
    if (addQty <= 0) { message.warning("Quantity must be greater than 0."); return; }
    if (addCost < 0) { message.warning("Cost cannot be negative."); return; }

    const exists = grnItems.some(i => Number(i.invi_id) === Number(selectedItemId));
    if (exists) { message.warning("This item is already added. Remove it first to re-add."); return; }

    const selected = inventoryItems.find(i => Number(i.invi_id) === Number(selectedItemId));
    const updated = [...grnItems, {
      invi_id: Number(selectedItemId),
      inv_code: selected?.inv_code || "",
      inv_desc: selected?.inv_desc || "",
      grn_qty: parseFloat(addQty),
      cost_price: parseFloat(addCost),
      tax_price: parseFloat(addTax),
      discount_price: parseFloat(addDiscount),
      is_adhoc: true
    }];
    setGrnItems(updated);
    syncGRNItemsToDB(updated);

    setSelectedItemId(null);
    setAddQty(1);
    setAddCost(0);
    setAddTax(0);
    setAddDiscount(0);
    message.success("Item added.");
  };

  const handleRemoveItem = (inviId) => {
    const updated = grnItems.filter(i => Number(i.invi_id) !== Number(inviId));
    setGrnItems(updated);
    syncGRNItemsToDB(updated);
    message.success("Item removed.");
  };

  const handleFieldChange = (inviId, field, value) => {
    setGrnItems(prev => prev.map(item =>
      Number(item.invi_id) === Number(inviId)
        ? { ...item, [field]: field === "grnitem_note" ? value : (parseFloat(value) || 0) }
        : item
    ));
  };

  const calculateTotals = () => {
    let subtotal = 0, totalTax = 0, totalDiscount = 0;
    grnItems.forEach(item => {
      const qty = parseFloat(item.grn_qty) || 0;
      subtotal += qty * (parseFloat(item.cost_price) || 0);
      totalTax += (parseFloat(item.tax_price) || 0);
      totalDiscount += (parseFloat(item.discount_price) || 0);
    });
    return { subtotal, tax: totalTax, discount: totalDiscount, grandTotal: subtotal + totalTax - totalDiscount };
  };

  const totals = calculateTotals();

  const onSaveGRN = async (status) => {
    if (status !== "Draft") {
      const validItems = grnItems.filter(i => i.invi_id && parseFloat(i.grn_qty) > 0);
      if (validItems.length === 0) { message.error("Add at least one item with qty > 0."); return; }
      if (!supRef || !supRef.trim()) { message.error("Please enter Supplier Reference."); return; }
    }

    const itemsToSend = status === "Draft"
      ? grnItems.filter(i => i.invi_id)
      : grnItems.filter(i => i.invi_id && parseFloat(i.grn_qty) > 0);

    setSaving(true);
    const payload = {
      grn_id: grnId ? parseInt(grnId) : undefined,
      pr_id: loadedGrnMaster ? parseInt(loadedGrnMaster.pr_id) : parseInt(poId),
      sup_ref_date: supRefDate ? supRefDate.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD"),
      grn_status: status,
      items: itemsToSend.map(i => ({
        invi_id: parseInt(i.invi_id),
        grn_qty: parseFloat(i.grn_qty || 0),
        cost_price: parseFloat(i.cost_price || 0),
        tax_price: parseFloat(i.tax_price || 0),
        discount_price: parseFloat(i.discount_price || 0),
        grnitem_note: i.grnitem_note || ""
      }))
    };
    if (poId === "0" || (loadedGrnMaster && Number(loadedGrnMaster.is_from_pr) === 0)) {
      payload.sup_id = loadedGrnMaster ? parseInt(loadedGrnMaster.sup_id) : parseInt(supplierId);
    }
    payload.sup_ref = supRef;

    try {
      const res = await api.post("/inventory/grn/create_from_pr.php", payload);
      if (res.data && res.data.status === "success") {
        message.success(res.data.message || "GRN created successfully.");
        navigate("/grn/list");
      } else {
        message.error(res.data.message || "Failed to create GRN.");
      }
    } catch (error) {
      message.error("Error creating GRN.");
    } finally {
      setSaving(false);
    }
  };

  const renderStatusTag = (status) => {
    return <Tag color={getStatusColor(status)} style={{ fontWeight: "600" }}>{status}</Tag>;
  };

  const selectedItemDetails = selectedItemId ? inventoryItems.find(i => Number(i.invi_id) === Number(selectedItemId)) : null;
  const selectedGrnItem = (poId !== "0" && selectedItemId) ? grnItems.find(gi => Number(gi.invi_id) === Number(selectedItemId)) : null;

  const itemColumns = [
    {
      title: "Item",
      key: "item_details",
      render: (_, record) => (
        <div>
          <strong>{record.inv_desc}</strong>
          <div style={{ fontSize: "12px", color: "#666" }}>
            Code: {record.inv_code}
          </div>
          {record.item_note && (
            <div style={{ fontSize: "12.5px", color: "#faad14", marginTop: 4, fontWeight: "500" }}>
              PR Note: {record.item_note}
            </div>
          )}
          {record.is_adhoc && <Tag color="geekblue" style={{ marginTop: 4, fontSize: 11 }}>Ad-hoc</Tag>}
        </div>
      )
    },
    ...(poId !== "0" ? [
      {
        title: "PR Requested", dataIndex: "ordered_qty", key: "ordered_qty", align: "right",
        render: (val, r) => r.is_adhoc ? "-" : Number(val || 0).toFixed(2)
      }
    ] : []),
    {
      title: "Qty Received", key: "grn_qty", width: 120,
      render: (_, record) => (
        <InputNumber
          min={0}
          value={record.grn_qty}
          style={{ width: "100%" }}
          onChange={(val) => handleFieldChange(record.invi_id, "grn_qty", val)}
          onBlur={() => syncGRNItemsToDB(grnItems)}
        />
      )
    },
    {
      title: "Unit Cost", key: "cost_price", width: 140,
      render: (_, record) => (
        <InputNumber min={0} value={record.cost_price} style={{ width: "100%" }}
          onChange={(val) => handleFieldChange(record.invi_id, "cost_price", val)}
          onBlur={() => syncGRNItemsToDB(grnItems)} />
      )
    },
    {
      title: "Tax", key: "tax_price", width: 120,
      render: (_, record) => (
        <InputNumber min={0} value={record.tax_price} style={{ width: "100%" }}
          onChange={(val) => handleFieldChange(record.invi_id, "tax_price", val)}
          onBlur={() => syncGRNItemsToDB(grnItems)} />
      )
    },
    {
      title: "Discount", key: "discount_price", width: 120,
      render: (_, record) => (
        <InputNumber min={0} value={record.discount_price} style={{ width: "100%" }}
          onChange={(val) => handleFieldChange(record.invi_id, "discount_price", val)}
          onBlur={() => syncGRNItemsToDB(grnItems)} />
      )
    },
    {
      title: "Note", key: "grnitem_note", width: 180,
      render: (_, record) => (
        <Input placeholder="Note" value={record.grnitem_note}
          onChange={(e) => handleFieldChange(record.invi_id, "grnitem_note", e.target.value)}
          onBlur={() => syncGRNItemsToDB(grnItems)} />
      )
    },
    {
      title: "Subtotal", key: "subtotal", align: "right", width: 150,
      render: (_, r) => {
        const sub = (parseFloat(r.grn_qty || 0) * parseFloat(r.cost_price || 0)) + parseFloat(r.tax_price || 0) - parseFloat(r.discount_price || 0);
        return <strong>LKR {sub.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>;
      }
    },
    {
      title: "Action", key: "action", align: "center", width: 140,
      render: (_, record) => {
        const isEditable = !loadedGrnMaster || loadedGrnMaster.grn_status === "Draft";
        return (
          <Space size="small">
            {isEditable && (
              <Popconfirm title="Remove this item?" onConfirm={() => handleRemoveItem(record.invi_id)}>
                <Button type="primary" danger icon={<DeleteOutlined />} size="small" />
              </Popconfirm>
            )}
          </Space>
        );
      }
    }
  ];

  return (
    <Layout className="fluid-page">
      <Headerdiv />

      <div className="fluid-page-header">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <Row align="bottom" justify="space-between">
          <Col>
            <AppBreadcrumb items={[
              { label: 'Inventory', link: '/inventory-items' },
              { label: 'Goods Received Note Verification' },
            ]} />
            <h1 className="fluid-page-title">
              <AuditOutlined /> Goods Received Note: {loadedGrnMaster ? loadedGrnMaster.grn_ref : (poId !== "0" && poMaster ? poMaster.pr_ref : "Direct Receipt")}
            </h1>
          </Col>
          <Col xs={0} md={12} style={{ textAlign: 'right' }}>
            <p className="fluid-page-subtitle">Verify received items and quantities before submission</p>
          </Col>
        </Row>
      </div>

      <Content className="fluid-content">
        <Spin spinning={loading} tip="Loading data...">
          {dataReady && (
            <Row gutter={[16, 16]}>
              {/* GRN & PO Overview Details */}
              <Col span={24}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={12}>
                    <Card className="fluid-card" title="GRN Overview" style={{ height: '100%' }}>
                      <Descriptions bordered size="small" column={1}>
                        {loadedGrnMaster && (
                          <Descriptions.Item label="GRN Reference">
                            <strong style={{ color: "var(--fluid-primary)" }}>{loadedGrnMaster.grn_ref}</strong>
                          </Descriptions.Item>
                        )}
                        {loadedGrnMaster && (
                          <Descriptions.Item label="GRN Create Date">
                            {loadedGrnMaster.created_dt
                              ? dayjs(loadedGrnMaster.created_dt).format("YYYY-MM-DD HH:mm")
                              : (loadedGrnMaster.grn_date ? dayjs(loadedGrnMaster.grn_date).format("YYYY-MM-DD") : "-")}
                          </Descriptions.Item>
                        )}
                        {loadedGrnMaster && (
                          <Descriptions.Item label="Created By">
                            {loadedGrnMaster.creator_name || "-"}
                          </Descriptions.Item>
                        )}
                        {loadedGrnMaster && (
                          <Descriptions.Item label="GRN Status">
                            {renderStatusTag(loadedGrnMaster.grn_status)}
                          </Descriptions.Item>
                        )}
                      </Descriptions>
                    </Card>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Card className="fluid-card" title="PO / Supplier Details" style={{ height: '100%' }}>
                      {poId !== "0" && poMaster ? (
                        <Descriptions bordered size="small" column={1}>
                          <Descriptions.Item label="Supplier Ref">
                            <strong>{supRef || "-"}</strong>
                          </Descriptions.Item>
                          <Descriptions.Item label="Supplier Ref Date">
                            {supRefDate ? supRefDate.format("YYYY-MM-DD") : "-"}
                          </Descriptions.Item>
                          <Descriptions.Item label="PR Reference">
                            <strong>{poMaster.pr_ref}</strong>
                          </Descriptions.Item>
                          <Descriptions.Item label="PR Date">
                            {dayjs(poMaster.pr_date).format("YYYY-MM-DD")}
                          </Descriptions.Item>
                          <Descriptions.Item label="Expected Delivery">
                            {poMaster.delivery_expected_date ? dayjs(poMaster.delivery_expected_date).format("YYYY-MM-DD") : "-"}
                          </Descriptions.Item>
                          <Descriptions.Item label="PR Status">
                            {renderStatusTag(poMaster.pr_status)}
                          </Descriptions.Item>
                        </Descriptions>
                      ) : (
                        <Descriptions bordered size="small" column={1}>
                          <Descriptions.Item label="Supplier Ref">
                            <strong>{supRef || "-"}</strong>
                          </Descriptions.Item>
                          <Descriptions.Item label="Supplier Ref Date">
                            {supRefDate ? supRefDate.format("YYYY-MM-DD") : "-"}
                          </Descriptions.Item>
                          <Descriptions.Item label="Date">
                            {dayjs().format("YYYY-MM-DD")}
                          </Descriptions.Item>
                        </Descriptions>
                      )}

                      {loadedGrnMaster && loadedGrnMaster.supplier_name && (
                        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--fluid-primary)' }}>Supplier</div>
                            <div style={{ fontSize: 15, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {loadedGrnMaster.supplier_name}
                            </div>
                          </div>
                          <Button icon={<EyeOutlined />} onClick={() => setSupplierModalVisible(true)}>
                            View Supplier
                          </Button>
                        </div>
                      )}
                    </Card>
                  </Col>
                </Row>
              </Col>

              {/* Add Item Card (ManagePRItems style) */}
              <Col span={24}>
                <Card className="fluid-card" title={<span><PlusOutlined style={{ marginRight: 8 }} />Add Item to Receipt</span>}>
                  <Row gutter={16} align="bottom" className="fluid-add-item-row">
                    <Col xs={24} md={8}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Select Inventory Item</label>
                      <Select allowClear
                        showSearch style={{ width: "100%" }}
                        placeholder="Search item by name or code"
                        optionFilterProp="children"
                        filterOption={(input, option) => String(option?.label || option?.children || '').toLowerCase().includes(input.toLowerCase())}
                        value={selectedItemId} onChange={handleItemSelect}
                      >
                        {inventoryItems.map(ii => (
                          <Option key={ii.invi_id} value={ii.invi_id} label={`${ii.inv_code} - ${ii.inv_desc}`}>
                            {ii.inv_code} - {ii.inv_desc}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                    <Col xs={12} md={4}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Quantity</label>
                      <InputNumber min={0.01} style={{ width: '100%' }} value={addQty} onChange={setAddQty} />
                    </Col>
                    <Col xs={12} md={3}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Cost</label>
                      <InputNumber min={0} style={{ width: '100%' }} value={addCost} onChange={setAddCost} />
                    </Col>
                    <Col xs={12} md={3}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Tax</label>
                      <InputNumber min={0} style={{ width: '100%' }} value={addTax} onChange={setAddTax} />
                    </Col>
                    <Col xs={12} md={3}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Discount</label>
                      <InputNumber min={0} style={{ width: '100%' }} value={addDiscount} onChange={setAddDiscount} />
                    </Col>
                    <Col xs={24} md={3}>
                      <Button type="primary" className="fluid-btn-primary" icon={<PlusOutlined />} onClick={handleAddItem} disabled={!selectedItemId} block>
                        Add Item
                      </Button>
                    </Col>
                  </Row>

                  {selectedItemDetails && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <InfoCircleOutlined style={{ color: 'var(--fluid-primary)' }} />
                        <span style={{ fontWeight: 600, color: '#262626' }}>Item Specifications</span>
                      </div>
                      <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: selectedGrnItem ? 5 : 4 }}>
                        <Descriptions.Item label="Item Code">{selectedItemDetails.inv_code}</Descriptions.Item>
                        <Descriptions.Item label="Description">{selectedItemDetails.inv_desc}</Descriptions.Item>
                        <Descriptions.Item label="Cost Price">{parseFloat(selectedItemDetails.cost_price || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                        {selectedGrnItem && (
                          <Descriptions.Item label="Max Order Qty">
                            <strong>{parseFloat(selectedGrnItem.ordered_qty || 0).toFixed(2)}</strong>
                          </Descriptions.Item>
                        )}
                        <Descriptions.Item label="Current Stock">
                          <strong style={{ color: parseInt(selectedItemDetails.available_qty) > 0 ? 'var(--fluid-primary)' : '#ff4d4f' }}>
                            {parseInt(selectedItemDetails.available_qty).toLocaleString()}
                          </strong>
                        </Descriptions.Item>
                      </Descriptions>
                    </div>
                  )}

                  {selectedItemId && (
                    <div style={{ marginTop: 12, fontSize: '15px', color: '#555' }}>
                      Estimated Item Total: <strong style={{ color: 'var(--fluid-primary)', fontSize: '16px' }}>
                        LKR {(((parseFloat(addQty) || 0) * (parseFloat(addCost) || 0)) + (parseFloat(addTax) || 0) - (parseFloat(addDiscount) || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </strong>
                    </div>
                  )}
                </Card>
              </Col>

              {/* Items Table */}
              <Col span={24}>
                <Card className="fluid-card" title={`Received Items (${grnItems.length})`}>
                  <Table
                    columns={itemColumns}
                    dataSource={grnItems}
                    rowKey="invi_id"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    size="small"
                    className="fluid-table"
                    bordered
                  />

                  {grnItems.length > 0 && (
                    <Card size="small" style={{ background: "#fafafa", marginTop: 16 }}>
                      <Row gutter={16} justify="end">
                        <Col xs={24} md={12}>
                          <Descriptions column={1} size="small" bordered>
                            <Descriptions.Item label="Sub Total">LKR {totals.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                            <Descriptions.Item label="Total Tax">LKR {totals.tax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                            <Descriptions.Item label="Total Discount">LKR {totals.discount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Descriptions.Item>
                            <Descriptions.Item label={<strong style={{ color: "var(--fluid-primary)" }}>Grand Total</strong>}>
                              <strong style={{ color: "var(--fluid-primary)" }}>LKR {totals.grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                            </Descriptions.Item>
                          </Descriptions>
                        </Col>
                      </Row>
                    </Card>
                  )}

                  <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end', paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
                    <Space size="middle">
                      <Button
                        type="default"
                        icon={<SaveOutlined />}
                        onClick={() => onSaveGRN("Draft")}
                        loading={saving}
                        style={{ minWidth: '150px' }}
                        disabled={grnItems.length === 0}
                      >
                        Save as Draft
                      </Button>
                      {grnItems.length > 0 && (
                        <Popconfirm
                          title="Submit this Goods Received Note for approval?"
                          onConfirm={() => onSaveGRN("Pending Approval")}
                        >
                          <Button
                            type="primary"
                            icon={<SendOutlined />}
                            loading={saving}
                            className="fluid-btn-primary"
                            style={{ minWidth: '150px' }}
                          >
                            Submit
                          </Button>
                        </Popconfirm>
                      )}
                    </Space>
                  </div>
                </Card>
              </Col>
            </Row>
          )}
        </Spin>
      </Content>
      <SupplierDetailsModal
        visible={supplierModalVisible}
        onCancel={() => setSupplierModalVisible(false)}
        supplier={loadedGrnMaster}
      />
      <Footerdiv />
    </Layout>
  );
};

export default ReceiveGRNItems;
